import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled, {css} from 'styled-components';

import Button from 'web-app/react/components/form-elements/button/button';
import ListPopover from 'web-app/react/components/popover/list-popover';
import Icon from 'web-app/react/components/icons/legacy/icon';
import ArrowIcon from 'web-app/react/components/icons/legacy/arrows';
import currentTheme from 'web-app/styleguide/themes';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import {Flex} from 'web-app/react/components/layout/layout';
import {s1, negative} from 'web-app/styleguide/spacing';

/**
 * @deprecated Use `app/react/components/button-v2/expandable-button.tsx` instead
 */
class ExpandableButton extends React.Component {
    constructor(props) {
        super(props);
        this.handleToggle = this.handleToggle.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.state = {
            show: false,
        };
    }
    handleClose() {
        this.setState({show: false});
    }

    handleToggle(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({show: !this.state.show});
    }

    render() {
        const {
            showArrow,
            items,
            expandPosition,
            className,
            mainButtonId,
            onClick,
            children,
            btnClassName,
            appearance,
            canCollapse,
            type,
            direction,
            alignItemContent,
            mainButtonClassName,
            listIconSize,
            border,
            disabled,
            onListClick,
            btnArrow,
            arrowDisabled,
            arrowPosition,
            e2eId,
            // The Box prop doesn't make sense to pass on as we wrap the
            // expandable button so it doesn't adhere to the rules of box
            box, // eslint-disable-line no-unused-vars
            ...passthroughProps
        } = this.props;

        const filteredItems = items.filter(_ => _);

        const mainButtonIsClickable = onClick || type === 'submit';

        const btnClick = onClick ? onClick : this.handleToggle;

        const shouldRender = mainButtonIsClickable || filteredItems.length;
        const shouldMakeCompact =
            !mainButtonIsClickable && filteredItems.length === 1 && filteredItems[0] && canCollapse;

        if (!shouldRender) {
            return null;
        }

        const arrowColor = currentTheme().buttonConfiguration[appearance]
            ? currentTheme().buttonConfiguration[appearance].color
            : undefined;

        const arrow =
            btnArrow === false ? null : (
                <ArrowIcon
                    direction={direction}
                    fill={arrowColor}
                    size={passthroughProps.buttonSize === 'mini' ? 16 : undefined}
                />
            );

        const renderDropDownButton = mainButtonIsClickable && items && items.length > 0;

        const showArrowSpinner = arrowDisabled !== undefined ? arrowDisabled : passthroughProps.spinner;
        const showItems = this.state.show && !disabled;

        return shouldMakeCompact ? (
            <div className={className}>
                {filteredItems[0].icon ? (
                    <Button
                        appearance="icon"
                        buttonSize={passthroughProps.buttonSize || 'mini'}
                        onClick={filteredItems[0].onClick}
                        className={btnClassName}
                        id={mainButtonId}
                        data-e2e-id={e2eId}
                    >
                        <StyledIcon icon={filteredItems[0].icon} alt={filteredItems[0].text} />
                    </Button>
                ) : (
                    <Button
                        {...passthroughProps}
                        disabled={disabled}
                        type={filteredItems[0].type || type}
                        appearance={filteredItems[0].appearance || appearance}
                        onClick={filteredItems[0].onClick}
                        className={btnClassName}
                        id={mainButtonId}
                        data-e2e-id={e2eId}
                    >
                        {filteredItems[0].compactTitle || filteredItems[0].title}
                    </Button>
                )}
            </div>
        ) : (
            <Flex relative className={className}>
                <StyledMainButton
                    {...passthroughProps}
                    noBorder={border === false}
                    className={classNames(btnClassName, mainButtonClassName, 'ignore-react-onclickoutside')}
                    disabled={disabled}
                    appearance={appearance}
                    type={type}
                    onClick={btnClick}
                    id={mainButtonId}
                    data-e2e-id={e2eId}
                    withDropDownButton={renderDropDownButton}
                    direction={direction}
                    active={this.state.show}
                >
                    <Flex align="center" justify="space-between">
                        <div>{children}</div>
                        {!mainButtonIsClickable && appearance !== 'more' && (
                            <Flex shrink={0} align="center" marginLeft={s1} marginRight={negative(s1)}>
                                {arrow}
                            </Flex>
                        )}
                    </Flex>
                </StyledMainButton>
                {renderDropDownButton && (
                    <StyledArrowButton
                        {...passthroughProps}
                        className="ignore-react-onclickoutside"
                        spinner={showArrowSpinner}
                        disabled={disabled}
                        appearance={appearance}
                        onClick={this.handleToggle}
                        direction={direction}
                        active={this.state.show}
                    >
                        {arrow}
                    </StyledArrowButton>
                )}
                {showItems ? (
                    <StyledPopoverWrapper position={expandPosition} direction={direction}>
                        <ListPopover
                            iconSize={listIconSize}
                            alignContent={alignItemContent}
                            position={expandPosition}
                            showArrow={showArrow}
                            onClose={this.handleClose}
                            items={filteredItems}
                            onClick={onListClick}
                        />
                    </StyledPopoverWrapper>
                ) : null}
            </Flex>
        );
    }
}

const buttonAppearances = [
    'default',
    'confirm',
    'secondary',
    'danger',
    'add',
    'delete',
    'deleteNoText',
    'icon',
    'custom',
    'link',
    'more',
    'none',
    'x',
    'primary',
    'profile',
    'asDefault',
    'textSecondary',
    'silent',
];

ExpandableButton.options = {
    expandPosition: ['right', 'left'],
    direction: ['north', 'south'],
    alignContent: ['center', 'left', 'right'],
    appearance: buttonAppearances,
};

ExpandableButton.defaultProps = {
    border: true,
    showArrow: false,
    direction: 'south',
    expandPosition: 'right',
};

ExpandableButton.propTypes = {
    disabled: PropTypes.bool,
    border: PropTypes.bool,
    showArrow: PropTypes.bool,
    btnArrow: PropTypes.bool,
    appearance: PropTypes.oneOf(buttonAppearances),
    items: ListPopover.propTypes.items,
    listIconSize: PropTypes.number,
    expandPosition: PropTypes.oneOf(ExpandableButton.options.expandPosition),
    alignItemContent: PropTypes.string,
    mainButtonClassName: PropTypes.string,
    direction: PropTypes.string,
    type: PropTypes.string,
    className: PropTypes.string,
    btnClassName: PropTypes.string,
    mainButtonId: PropTypes.string,
    onClick: PropTypes.func,
    canCollapse: PropTypes.bool,
    children: PropTypes.node,
    onListClick: PropTypes.func,
    arrowDisabled: PropTypes.bool,
    e2eId: PropTypes.string,
};

export default ExpandableButton;

// eslint-disable-next-line react/prop-types
export const StylableExpandableButton = ({className, ...rest}) => (
    <ExpandableButton mainButtonClassName={className} {...rest} />
);
StylableExpandableButton.propTypes = ExpandableButton.propTypes;

/* Styling */
const activeBorderRadiusStyle = css`
    ${props => {
        if (props.active) {
            switch (props.direction) {
                case 'north':
                    return css`
                        border-top-right-radius: 0;
                        border-top-left-radius: 0;
                    `;
                case 'south':
                default:
                    return css`
                        border-bottom-right-radius: 0;
                        border-bottom-left-radius: 0;
                    `;
            }
        }
        return '';
    }}
`;

export const StyledMainButton = styled(Button)`
    ${activeBorderRadiusStyle}
    ${props =>
        props.noBorder
            ? css`
                  border: 0 !important;
              `
            : ''}
    ${props =>
        props.withDropDownButton
            ? css`
                  border-top-right-radius: 0;
                  border-bottom-right-radius: 0;
                  ${props.appearance === 'confirm'
                      ? css`
                            border-right: 1px solid rgba(255, 255, 255, 0.5) !important;
                        `
                      : ''}
              `
            : ''}
`;

const StyledIcon = styled(Icon)`
    height: 20px;
    width: 20px;
`;

const StyledPopoverWrapper = styled.div`
    position: absolute;
    min-width: 100%;
    z-index: 999;
    box-sizing: border-box;
    background: white;
    button {
        border: 1px solid #ddd;
        & + button {
            border-top: 0;
        }
        &:last-of-type {
            border-bottom: 1px solid #ddd;
        }
    }

    ${props => {
        switch (props.position) {
            case 'right':
                return css`
                    right: 0;
                    text-align: right;
                `;
            case 'left':
                return css`
                    left: 0;
                    text-align: left;
                `;
            default:
                return null;
        }
    }}

    ${props => {
        switch (props.direction) {
            case 'north':
                return css`
                    top: 1px;
                    transform: translate(0, -100%);
                    box-shadow: 0px -1px 2px 1px rgba(0, 0, 0, 0.1);
                    border-radius: 4px 4px 0 0;
                    button:last-of-type {
                        border-bottom-right-radius: 0;
                        border-bottom-left-radius: 0;
                    }
                `;
            case 'south':
            default:
                return css`
                    bottom: 1px;
                    transform: translate(0, 100%);
                    box-shadow: 0px 1px 2px 1px rgba(0, 0, 0, 0.1);
                    border-radius: 0 0 4px 4px;
                    button:first-of-type {
                        border-top-right-radius: 0;
                        border-top-left-radius: 0;
                    }
                `;
        }
    }}
`;

const StyledArrowButton = styled(Button)`
    display: flex;
    justify-content: center;
    align-items: center;

    padding-left: 8px;
    padding-right: 8px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    &,
    &:active,
    &:focus {
        border-left: 0;
    }

    ${Breakpoints.tabletLandscapeAndLarger`
        padding-left: 2px;
        padding-right: 2px;
    `}

    ${activeBorderRadiusStyle}
`;
